import { useEffect, useState } from 'react';
import { Loader2, Eye, Columns2, RotateCcw, Wand2, Undo2, ZoomIn, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { toast } from '@/components/ui/sonner';
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider';
import { adminColorPreview, adminColorApply, adminColorRevert } from '@/lib/api';
import { COLOR } from '@/constants/testIds';

const approvedValues = (decisions) => {
  const out = {};
  Object.entries(decisions || {}).forEach(([k, d]) => {
    if ((d.state === 'accepted' || d.state === 'modified') && Math.abs(Number(d.value) || 0) >= 0.01) out[k] = Number(d.value);
  });
  return out;
};

const ZoomPane = ({ src, label, zoom, testId }) => (
  <figure data-testid={testId} className="overflow-hidden rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416]">
    <div className="relative aspect-[4/3] overflow-hidden">
      <img src={src} alt={label} style={{ transform: `scale(${zoom})` }} className="h-full w-full object-contain transition-transform duration-150 will-change-transform" />
    </div>
    <figcaption className="px-2 py-1 text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">{label}</figcaption>
  </figure>
);

/**
 * CI-2 — before/after preview, non-destructive apply and one-click revert.
 * The preview is rendered in memory; "Render graded copy" stores a derived
 * variant; "Revert" deletes only that derived copy. Originals are untouched.
 */
export const ApplyPanel = ({ image, slug, onChanged, disabled }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('slider');
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const approved = approvedValues(image?.color?.decisions);
  const approvedKey = JSON.stringify(approved);
  const hasApproved = Object.keys(approved).length > 0;
  const applied = image?.color?.applied;
  const stale = applied && JSON.stringify(applied.values || {}) !== approvedKey;

  useEffect(() => { setPreview(null); setZoom(1); }, [image?.id, approvedKey]);

  const loadPreview = async () => {
    setLoading(true);
    try { setPreview(await adminColorPreview(slug, image.id, {})); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not render the preview.'); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    setBusy(true);
    try { await adminColorApply(slug, image.id); toast.success('Graded copy rendered. The original is untouched.'); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not render the graded copy.'); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    setBusy(true);
    try { await adminColorRevert(slug, image.id); toast.success('Reverted — graded copy removed.'); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not revert.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0B0B0C] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">Before / after & apply</h4>
        {applied && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] ${stale ? 'border-amber-300/50 text-amber-200' : 'border-emerald-400/40 text-emerald-200'}`}>
            <CheckCircle2 className="h-3 w-3" /> {stale ? 'Graded copy out of date' : 'Graded copy stored'}
          </span>
        )}
      </div>
      {!hasApproved && !applied && <p className="mt-2 text-[11px] text-[hsl(var(--lxra-muted))]">Accept or modify at least one recommendation to preview and render this photo.</p>}

      {hasApproved && !preview && (
        <Button size="sm" variant="outline" data-testid={COLOR.previewButton} onClick={loadPreview} disabled={loading || disabled} className="mt-3 h-8 border-[hsl(var(--lxra-border))] bg-transparent text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]">
          {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />} Preview approved moves
        </Button>
      )}

      {preview && (
        <div data-testid={COLOR.previewPanel} className="mt-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] p-0.5">
              {[['slider', 'Slider', RotateCcw], ['side', 'Side by side', Columns2]].map(([k, label, Icon]) => (
                <button key={k} type="button" data-testid={`${COLOR.previewModeToggle}-${k}`} aria-pressed={mode === k} onClick={() => setMode(k)} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] uppercase tracking-[0.08em] transition-colors ${mode === k ? 'bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]' : 'text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]'}`}><Icon className="h-3 w-3" /> {label}</button>
              ))}
            </div>
            {mode === 'side' && (
              <div className="flex items-center gap-2">
                <ZoomIn className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" />
                <Slider data-testid={COLOR.previewZoom} value={[zoom]} min={1} max={3} step={0.1} onValueChange={([v]) => setZoom(v)} className="w-28" aria-label="Zoom" />
                <span className="w-8 font-mono text-[10px] text-[hsl(var(--lxra-muted))]">{zoom.toFixed(1)}×</span>
              </div>
            )}
            <Button size="sm" variant="ghost" data-testid={COLOR.previewReset} onClick={() => { setPreview(null); setZoom(1); }} className="ml-auto h-7 px-2 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]">Reset</Button>
          </div>
          {mode === 'slider' ? (
            <div data-testid={COLOR.previewSlider}>
              <BeforeAfterSlider before={{ image_url: preview.before }} after={{ image_url: preview.after }} caption="Drag the handle — left is the original, right is the approved grade." testId={`${COLOR.previewSlider}-widget`} />
            </div>
          ) : (
            <div data-testid={COLOR.previewSideBySide} className="grid grid-cols-2 gap-2">
              <ZoomPane src={preview.before} label="Original" zoom={zoom} testId={`${COLOR.previewSideBySide}-before`} />
              <ZoomPane src={preview.after} label="AI recommendation" zoom={zoom} testId={`${COLOR.previewSideBySide}-after`} />
            </div>
          )}
          <p className="text-[10px] text-[hsl(var(--lxra-muted))]">Preview at {preview.width}×{preview.height} · applying: {Object.keys(preview.values).length} move{Object.keys(preview.values).length === 1 ? '' : 's'}</p>
        </div>
      )}

      {(hasApproved || applied) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {hasApproved && (
            <Button size="sm" data-testid={COLOR.applyButton} onClick={apply} disabled={busy || disabled || (applied && !stale)} className="h-8 bg-[hsl(var(--lxra-gold))] px-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-40">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />} {applied ? (stale ? 'Re-render graded copy' : 'Graded copy up to date') : 'Render graded copy'}
            </Button>
          )}
          {applied && (
            <Button size="sm" variant="ghost" data-testid={COLOR.revertButton} onClick={revert} disabled={busy || disabled} className="h-8 px-3 text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:bg-[#1a1a1c] hover:text-red-300">
              <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Revert to original
            </Button>
          )}
          {applied && <a href={applied.url} target="_blank" rel="noreferrer" className="text-[10px] text-[hsl(var(--lxra-muted))] underline-offset-2 hover:text-[hsl(var(--lxra-gold))] hover:underline">Open graded file</a>}
        </div>
      )}
    </div>
  );
};
